// SlimmeActieTile — Dashboard cycle #50 (Bram feedback 2026-05-22).
//
// "Slimme actie" was een grote tegel met weinig tekst (single regel) en veel
// deadspace. Hier vullen we die ruimte met:
//   - Huidige EMS-engagement (yield / clamp / discharge etc.) — wat doet de
//     planner NU?
//   - Eerstvolgende geplande actie (laden / ontladen) + tijd + prijs
//   - Daaropvolgende tegenactie (laden → ontladen / ontladen → laden) — toont
//     de "spread" zodat gebruiker ziet WAAROM de planner laadt/ontlaadt.
//
// Layout (mobile full-width via .kpi-tile-double):
//   Rij 1: label "Slimme actie"
//   Rij 2: hoofdwaarde (volgende actie) — bv "Laden vanaf 12:00"
//   Rij 3: prijs-context + spread (waarom)
//   Rij 4: nu-status (huidige engagement) — kleinere typografie

import './kpi.css';
import type { CSSProperties } from 'react';
import { fmtEurPerKwh } from '../../lib/format';
import type { SocForecastResponse, SocForecastHour, HealthResponse } from '../../api/types';

const ACTION_NL: Record<string, string> = {
  charge: 'Laden',
  discharge: 'Ontladen',
  idle: 'Eigen verbruik',
};
const ACTION_REVERSE: Record<string, 'charge' | 'discharge'> = {
  charge: 'discharge',
  discharge: 'charge',
};

const ENGAGEMENT_NL: Record<string, string> = {
  yield: 'Solis self-use',
  clamp: 'Plafond actief',
  discharge: 'Ontladen actief',
  charge: 'Laden actief',
  force_yield: 'Anti-flap rust',
  off: 'Geen engagement',
  pv_only: 'Alleen PV',
};

interface Props {
  socForecast: SocForecastResponse | null;
  health: HealthResponse | null;
}

export function SlimmeActieTile({ socForecast, health }: Props) {
  const now = new Date();
  const traj = socForecast?.trajectory ?? [];

  // Volgende actieve actie (charge of discharge) — slaat 'idle' uren over.
  const nextAction: SocForecastHour | undefined = traj.find(
    (t) => new Date(t.hour_start) > now && t.action !== 'idle',
  );
  // Tegenactie: na nextAction zoeken we de eerste tegenactie. Bv. na charge
  // zoeken we de eerstvolgende discharge. Geeft inzicht in de spread.
  let counterAction: SocForecastHour | undefined;
  if (nextAction) {
    const reverse = ACTION_REVERSE[nextAction.action];
    const idxNext = traj.indexOf(nextAction);
    counterAction = traj
      .slice(idxNext + 1)
      .find((t) => t.action === reverse);
  }

  // Huidige engagement uit health-endpoint. Fallback naar 'onbekend'.
  const engagementKey = typeof health?.ems_action_engagement === 'string'
    ? health.ems_action_engagement
    : null;
  const engagementLabel = engagementKey
    ? ENGAGEMENT_NL[engagementKey] ?? engagementKey
    : null;

  // Bereken spread voor educatieve subline. positief = winstgevend.
  // Bij next=charge: koop laag, verkoop hoog → spread = nextDischarge.sell - nextCharge.buy
  // Bij next=discharge: verkoop hoog, kocht laag → spread = next.sell - counter.buy
  let spreadEurKwh: number | null = null;
  if (nextAction && counterAction) {
    if (nextAction.action === 'charge') {
      spreadEurKwh = (counterAction.sell_price ?? 0) - (nextAction.buy_price ?? 0);
    } else {
      spreadEurKwh = (nextAction.sell_price ?? 0) - (counterAction.buy_price ?? 0);
    }
  }

  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });

  return (
    <div
      className="kpi-tile slimme-actie-tile"
      style={{ ['--accent' as string]: '#c084fc' } as CSSProperties}
      title={[
        `Huidige engagement: ${engagementLabel ?? 'onbekend'}`,
        nextAction
          ? `Volgende actie: ${ACTION_NL[nextAction.action]} ${fmtTime(nextAction.hour_start)} → SOC ${nextAction.soc_end_pct.toFixed(0)}%`
          : 'Geen geplande actie binnen 48u',
        counterAction
          ? `Tegenactie: ${ACTION_NL[counterAction.action]} ${fmtTime(counterAction.hour_start)} bij ${fmtEurPerKwh(counterAction.action === 'charge' ? counterAction.buy_price : counterAction.sell_price)}`
          : '',
        health?.ems_action_reason ? `Reden: ${health.ems_action_reason}` : '',
      ].filter(Boolean).join('\n')}
    >
      <div className="kpi-label">Slimme actie</div>
      {nextAction ? (
        <>
          <div className="kpi-value slimme-actie-main">
            <span className={`slimme-actie-arrow slimme-actie-arrow--${nextAction.action}`} aria-hidden>
              {nextAction.action === 'charge' ? '▲' : '▼'}
            </span>
            {ACTION_NL[nextAction.action]}{' '}
            <span className="slimme-actie-time">{fmtTime(nextAction.hour_start)}</span>
          </div>
          <div className="slimme-actie-context">
            <span>
              tot SOC {nextAction.soc_end_pct.toFixed(0)}% · {fmtEurPerKwh(
                nextAction.action === 'charge' ? nextAction.buy_price : nextAction.sell_price,
              )}
            </span>
          </div>
          {counterAction && spreadEurKwh != null && spreadEurKwh > 0 && (
            <div className="slimme-actie-spread">
              <span className="slimme-actie-arrow-mini" aria-hidden>→</span>
              <span>
                {ACTION_NL[counterAction.action]} {fmtTime(counterAction.hour_start)} ·{' '}
                {fmtEurPerKwh(
                  counterAction.action === 'discharge' ? counterAction.sell_price : counterAction.buy_price,
                )}
                <span className="slimme-actie-spread-gain">
                  {` (+${fmtEurPerKwh(spreadEurKwh).replace('€ ', '€ ').replace('/kWh', '/kWh winst')})`}
                </span>
              </span>
            </div>
          )}
        </>
      ) : (
        <div className="kpi-value small">geen actie volgend uur</div>
      )}
      {engagementLabel && (
        <div className="slimme-actie-now">
          <span className="slimme-actie-now-label">Nu</span>
          <span className="slimme-actie-now-value">{engagementLabel}</span>
        </div>
      )}
    </div>
  );
}
